/* global globalThis */
/**
 * @fileoverview Member Quickview Component
 * @module components/members/MemberQuickview
 * @description A comprehensive member quickview panel that displays member details,
 * opportunities, and notes in a modal overlay.
 *
 * @requires react
 * @requires @mui/material
 * @requires @mui/icons-material/Close
 * @requires ../cards/NotesCard
 * @requires ../opportunities/TopOpportunities
 * @requires ../../services/mockDataService
 * @requires ../../api/quickview
 * @requires ../../api/priority
 * @requires ../../api/oppurtunity
 * @requires ../../api/api
 */

import { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import AccessibleSpinner from "../common/AccessibleSpinner";
import CloseIcon from "@mui/icons-material/Close";
import NotesCard from "../cards/NotesCard";
import { styled } from "@mui/material/styles";
import TopOpportunities from "../opportunities/TopOpportunities";
import { useSelector } from "react-redux";

import { fetchMemberOverview } from "../../api/priority";
import { fetchOpportunities } from "../../api/oppurtunity";
import api from "../../api/api";
import { useLoading } from "../../context/LoadingContext";
import {
  getNameVariants,
  maskAllButLast,
  maskEmail,
  maskShowInitialOnly,
  maskAll,
  maskCity,
  maskAddressLine,
  maskState,
  maskPostalCode,
  maskDOB,
} from "../../utils/maskedData";
import { formatPhoneNumber } from "../../utils/formatUtils";
import { capitalizeFirstLetter } from "../../utils/capitalize";

/**
 * Parses a drug name string to extract the base name and strength
 * @param {string} drugName - The full drug name string to parse
 * @returns {Object} An object containing the parsed name and strength
 * @property {string} name - The base name of the drug
 * @property {string} strength - The extracted strength information
 */

/**
 * Styled component for the modal overlay
 * @constant {Object} Overlay
 * @description Creates a semi-transparent overlay that covers the entire viewport
 */
const Overlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1200,
  overflow: "hidden",
});

/**
 * Styled component for the main popup container
 * @constant {Object} PopupContainer
 * @description Creates a responsive popup container with slide-in animation
 */
const PopupContainer = styled(Box)(({ theme }) => ({
  width: "90vw",
  maxWidth: "1250px",
  maxHeight: "90vh", // 👈 key for responsiveness
  minHeight: "400px",
  backgroundColor: "#F7F8FB",
  borderRadius: "16px",
  boxShadow: "4px 0 20px rgba(0, 0, 0, 0.15)",
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-100%, -50%)",
  animation: "slideIn 0.3s ease-out forwards",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  zIndex: 1300,

  [theme.breakpoints.down("md")]: {
    width: "calc(100% - 16px)",
    maxHeight: "95vh",
    minHeight: "90vh",
  },

  "@keyframes slideIn": {
    from: { transform: "translate(-100%, -50%)" },
    to: { transform: "translate(-50%, -50%)" },
  },
}));

/**
 * Styled component for the inner content wrapper
 * @constant {Object} ContentWrapper
 * @description Wraps the main content area with proper spacing and scrolling
 */
const ContentWrapper = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: "20px 24px 24px",
  gap: "16px",
  backgroundColor: "#F7F8FB",
  overflowY: "auto",
});

/**
 * MemberQuickview Component
 *
 * Displays a detailed view of member information including opportunities, notes, and program status.
 * Can be used in different contexts based on the source prop.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Callback function to close the quickview
 * @param {Object} props.memberData - Member data object containing member information
 * @param {Function} props.onNoteUpdate - Callback function when notes are updated
 * @param {string} [props.source='patientCareMonitoring'] - The source context for the quickview ('patientCareMonitoring' or 'patientCareOpportunities')
 * @returns {JSX.Element} The rendered MemberQuickview component
 */
const MemberQuickview = ({
  onClose,
  memberData: propMemberData,
  onNoteUpdate,
  source = "memberMonitoring",
}) => {
  const [selectedNoteType, setSelectedNoteType] = useState([]);
  const [memberData, setMemberData] = useState(propMemberData.member || null);
  const [notesSearchQuery, setNotesSearchQuery] = useState("");
  const [opportunities, setOpportunities] = useState([]);
  const [programStatusMap, setProgramStatusMap] = useState({});
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState({
    member: true,
    opportunities: true,
    notes: true,
  });
  const [error, setError] = useState(null);

  const { showLoading, hideLoading } = useLoading();
  const isShowPhiEnabled = !useSelector((state) => state.toggles.phi);
  /**
   * Fetches member opportunities data from the API
   * @async
   * @param {string|number} memberId - The ID of the member to fetch opportunities for
   * @returns {Promise<void>}
   */
  const fetchMemberOpportunities = async (memberId) => {
    try {
      const response = await fetchOpportunities(memberId);

      let opportunitiesData = [];
      if (Array.isArray(response)) {
        opportunitiesData = response;
      } else if (response?.content) {
        opportunitiesData = response.content;
      } else if (response?.data) {
        opportunitiesData = response.data;
      }

      if (!opportunitiesData?.length) {
        setOpportunities([]);
        setLoading((prev) => ({ ...prev, opportunities: false }));
        return;
      }

      const sortedOpportunities = [...opportunitiesData].sort((a, b) => {
        const timeA = a.recentActivity?.timestamp
          ? new Date(a.recentActivity.timestamp)
          : new Date(0);
        const timeB = b.recentActivity?.timestamp
          ? new Date(b.recentActivity.timestamp)
          : new Date(0);
        return timeB - timeA; // Sort in descending order (newest first)
      });

      // Helper: Get drug name from various sources
      const getDrugName = (item, medication) => {
        return (
          item.medicationName ||
          medication.medicationName ||
          item.drug ||
          medication.drugName ||
          ""
        );
      };

      // Take all opportunities sorted by most recent activity
      const topOpportunities = sortedOpportunities.map((item) => {
        const medication = item.medication || {};
        const program = item.program || {};
        const provider = item.provider || {};
        const graduated = item.isGraduated;

        const drugName = getDrugName(item, medication);
        const strength = program.strength || "N/A";
        const memberCost = program.memberCost || item.memberCost;
        const upperStatus = program.status?.toUpperCase();
        const formattedMemberCost = (() => {
          if (memberCost?.toString().startsWith("$")) {
            return memberCost;
          }
          if (memberCost) {
            return `$${Number.parseFloat(memberCost).toFixed(2)}`;
          }
          return "";
        })();

        // Create the opportunity object
        return {
          id: program.id || item.id,
          category: program.programName || item.category,
          drug: drugName,
          medicationName: drugName,
          strength: program.strength || strength,

          metricQuantity:
            program.metricQty === null ? "N/A" : program.metricQty,

          daysSupply:
            program.daySupply === undefined ? "N/A" : program.daySupply,
          pdc: program.pdc,
          potency: medication.potency,
          potencyUnit: medication.potencyUnit,
          cost: formattedMemberCost,
          lastFilled: program.lastFilled,
          provider: provider.name,
          providerPhone: provider.contact,
          dateAdded: program.dateAdded,
          pharmacy: program.pharmacy?.name,
          status: upperStatus,
          memberCost: formattedMemberCost,
          monitoringReason: item.monitoringReason,
          programName: program.programName,
          programStatus: program.status?.toUpperCase(),
          drugNdc: medication?.drugNdc,
          recentActivity: {
            status: upperStatus,
            at: program.lastFilled,
            statusColor: (() => {
              const status = program.status?.toUpperCase();
              if (status === "ELIGIBLE") return "#FF9800";
              if (status === "ENROLLED") return "#4CAF50";
              return "#F44336";
            })(),
          },

          providerName: provider.name,
          pharmacyName: program.pharmacy?.name,
          dateOfNearestExpiry: program.dateAdded,
          isGraduated: graduated,
        };
      });

      setOpportunities(topOpportunities);

      const programStatusMap = {};

      for (const item of topOpportunities) {
        if (item.programName && item.programStatus) {
          programStatusMap[item.programName] = {
            name: item.programName,
            status: item.programStatus,
          };
        }
      }

      setProgramStatusMap(programStatusMap);

      setLoading((prev) => ({ ...prev, opportunities: false }));
    } catch (err) {
      setError(`Error loading opportunities: ${err.message}`);
      setOpportunities([]);
      setLoading((prev) => ({ ...prev, opportunities: false }));
    }
  };

  const fetchNotes = async (
    memberId,
    searchQuery = "",
    entityType = "MEMBER",
  ) => {
    try {
      const params = new URLSearchParams({
        entityType,
        entityId: memberId,
      });

      if (searchQuery && searchQuery.trim() !== "") {
        params.append("searchQuery", searchQuery.trim());
      }

      const url = `/api/notes?${params.toString()}`;

      const response = await api.get(url, {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      });

      let notesData = [];
      const responseData = response.data;

      if (Array.isArray(responseData)) {
        notesData = [...responseData];
      } else if (Array.isArray(responseData?.content)) {
        notesData = [...responseData.content];
      } else if (Array.isArray(responseData?.latest)) {
        notesData = [...responseData.latest];
      } else if (responseData?.data && Array.isArray(responseData.data)) {
        notesData = [...responseData.data];
      } else if (responseData && typeof responseData === "object") {
        notesData = [responseData];
      }

      notesData = notesData.sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
      );

      const formattedNotes = notesData.map((note) => {
        let authorName = "System";
        if (note.author) {
          authorName =
            typeof note.author === "number"
              ? note.author.toString()
              : note.author;
        } else if (note.createdBy) {
          authorName =
            typeof note.createdBy === "number"
              ? note.createdBy.toString()
              : note.createdBy;
        }

        const noteType = (() => {
          const types = [];
          if (note.noteType) types.push(note.noteType);
          if (note.type) types.push(note.type);

          return [...new Set(types)][0] || "General";
        })();

        return {
          ...note,

          content: note.noteEntry || note.content || "",
          type: noteType,
          author: authorName,

          createdAt: note.createdAt,
          updateAt: note.updateAt || note.updatedAt || null,
          updatedAt: note.updateAt || note.updatedAt || null,

          pinNote: note.pinned || false,
          id: note.noteId || note.id,
          rating: note.rating,
        };
      });
      const filteredFormattedNotes = formattedNotes.filter(
        (note) => note.content?.trim() !== "",
      );
      setNotes(filteredFormattedNotes);
      setLoading((prev) => ({ ...prev, notes: false }));
    } catch (err) {
      setError(`Error loading notes: ${err.message}`);
      setNotes([]);
      setLoading((prev) => ({ ...prev, notes: false }));
    }
  };

  const handleNotesSearch = (query) => {
    setNotesSearchQuery(query);

    const memberId = propMemberData?.memberId || propMemberData?.id;
    if (memberId) {
      void fetchNotes(memberId, query, "MEMBER");
    }
  };

  const fetchQuickViewData = useCallback(async () => {
    if (!propMemberData?.memberId && !propMemberData?.id) {
      setError("No member ID provided");
      setLoading({ member: false, opportunities: false, notes: false });
      return;
    }

    const memberId = propMemberData.memberId || propMemberData.id;

    if (!memberId || !loading.member) return;

    setError(null);

    try {
      showLoading();

      const memberOverview = await fetchMemberOverview(memberId);

      await Promise.all([
        fetchMemberOpportunities(memberId),
        fetchNotes(memberId, "", "MEMBER"),
      ]);

      const cleanData = {
        ...propMemberData,

        ...memberOverview,

        name: memberOverview?.name || propMemberData?.name || "",
        gender: (() => {
          const gender = memberOverview?.gender || propMemberData?.gender || "";
          return gender &&
            !["unknown", "undefined", ""].includes(gender.toLowerCase())
            ? gender
            : "N/A";
        })(),
        dateOfBirth:
          memberOverview?.dateOfBirth ||
          memberOverview?.dob ||
          propMemberData?.dateOfBirth ||
          propMemberData?.dob ||
          "",
        dispositionStatus: memberOverview?.dispositionStatus || "",
        contact: {
          phone:
            memberOverview?.phone ||
            memberOverview?.contact?.phone ||
            propMemberData?.contact?.phone ||
            "",
          email: (() => {
            const email =
              memberOverview?.email ||
              memberOverview?.contact?.email ||
              propMemberData?.contact?.email;
            return email && !email.includes("undefined") ? email : "N/A";
          })(),
          language:
            memberOverview?.language ||
            memberOverview?.contact?.language ||
            propMemberData?.contact?.language ||
            propMemberData?.language ||
            "N/A",
          timeZone:
            memberOverview?.timeZone ||
            memberOverview?.contact?.timeZone ||
            propMemberData?.contact?.timeZone ||
            propMemberData?.timeZone ||
            "N/A",
          address:
            memberOverview?.address ||
            memberOverview?.contact?.address ||
            propMemberData?.contact?.address ||
            "",
        },

        memberId: memberId,
        id: memberId,
      };

      setMemberData(cleanData);
      setLoading((prev) => ({ ...prev, member: false }));
    } catch (err) {
      hideLoading();

      setError(
        "Failed to load member details. Please try again.",
        err?.message,
      );
      setLoading((prev) => ({ ...prev, member: false }));
    } finally {
      hideLoading();
    }
    /* eslint-disable no-unused-vars */
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [propMemberData, loading.member]);

  useEffect(() => {
    if (!propMemberData) {
      setLoading({ member: false, opportunities: false, notes: false });
      setError("No member data provided");
      return;
    }

    const memberId = propMemberData.memberId || propMemberData.id;
    const cleanData = {
      memberId: memberId || "",
      id: memberId || "",
      name: propMemberData.name || propMemberData.member?.name || "",
      language:
        propMemberData.language || propMemberData.contact?.language || "",
      timeZone:
        propMemberData.timeZone || propMemberData.contact?.timeZone || "",
      gender: (() => {
        const genderValue = propMemberData?.sex || propMemberData?.gender;

        const contactGender =
          propMemberData?.contact?.sex || propMemberData?.contact?.gender;

        const finalValue = genderValue || contactGender;

        if (
          !finalValue ||
          String(finalValue).trim() === "" ||
          ["unknown", "undefined"].includes(String(finalValue).toLowerCase())
        ) {
          return "N/A";
        }

        return (
          String(finalValue).charAt(0).toUpperCase() +
          String(finalValue).slice(1).toLowerCase()
        );
      })(),
      age: propMemberData.age || "",
      dob: propMemberData.dob || propMemberData.dateOfBirth || "",
      dateOfBirth: propMemberData.dateOfBirth || propMemberData.dob || "",
      knownAllergies: Array.isArray(propMemberData.knownAllergies)
        ? propMemberData.knownAllergies
        : [],
      allergies: Array.isArray(propMemberData.allergies)
        ? propMemberData.allergies
        : [],
      status: propMemberData.status || "",
      contact: {
        phone: propMemberData.contact?.phone || "",
        email:
          propMemberData.contact?.email &&
          !propMemberData.contact.email.includes("undefined")
            ? propMemberData.contact.email
            : "N/A",
        language:
          propMemberData.contact?.language || propMemberData.language || "",
        timeZone:
          propMemberData.contact?.timeZone || propMemberData.timeZone || "",
        address: propMemberData.contact?.address || "",
      },
    };

    setMemberData(cleanData);

    if (memberId) {
      setLoading({
        member: true,
        opportunities: true,
        notes: true,
      });

      fetchQuickViewData();
    } else {
      setLoading({ member: false, opportunities: false, notes: false });
    }
    /* eslint-disable no-unused-vars */
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [propMemberData?.memberId, propMemberData?.id]);

  useEffect(() => {
    const originalStyle = globalThis.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    globalThis.addEventListener("keydown", handleEscape);
    return () => globalThis.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  let notesContent;
  if (loading.notes) {
    notesContent = (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <AccessibleSpinner size={24} label="Loading notes" />
      </Box>
    );
  } else if (error) {
    notesContent = (
      <Box p={2} color="error.main">
        {error}
      </Box>
    );
  } else {
    notesContent = (
      <NotesCard
        notes={notes}
        selectedNoteType={selectedNoteType}
        searchQuery={notesSearchQuery}
        onNoteTypeChange={setSelectedNoteType}
        onSearch={handleNotesSearch}
        programStatusMap={programStatusMap}
        onTogglePin={(noteId) => {
          const updatedNotes = notes.map((note) =>
            note.id === noteId ? { ...note, pinned: !note.pinned } : note,
          );
          setNotes(updatedNotes);
          if (onNoteUpdate) onNoteUpdate(updatedNotes);
        }}
        inQuickview={true}
      />
    );
  }
  return (
    <Overlay onClick={onClose}>
      <PopupContainer
        component="section"
        aria-modal="true"
        aria-label="Member quickview"
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            borderRadius: "16px",
            overflow: "visible",
            backgroundColor: "#ECECF1",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            zIndex: 1600,
            maxHeight: "inherit",
          }}
        >
          <ContentWrapper>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "20px !important",
                  color: "#403C6F",
                }}
              >
                Member Quickview
              </Typography>
              <IconButton
                onClick={onClose}
                aria-label="Close"
                sx={{
                  color: "#403C6F",
                  marginLeft: "16px",
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "16px",
                backgroundColor: "white",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  sx={{ fontSize: "18px", fontWeight: 600, color: "#403C6F" }}
                >
                  {memberData?.name
                    ? getNameVariants(memberData.name, isShowPhiEnabled)
                        .maskedMiddle
                    : ""}
                </Typography>
                <Chip
                  label={memberData?.status || ""}
                  size="small"
                  sx={{
                    bgcolor: "#E8F5E9",
                    color: "#1B5E20",
                    fontWeight: 500,
                    fontSize: "12px",
                    borderRadius: "12px",
                    height: "24px",
                    border: "1px solid #1B5E20",
                    boxSizing: "border-box",
                  }}
                />
                <Chip
                  label={memberData?.dispositionStatus}
                  color="success"
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#ECECF1",
                    color: "#403C6F",
                    border: 0,
                    height: "24px",
                    "& .MuiChip-label": {
                      padding: "0 8px",
                      fontSize: "0.75rem",
                    },
                  }}
                />
                {memberData?.doNotCall && (
                  <Chip
                    label="Do Not Call"
                    size="small"
                    sx={{
                      backgroundColor: "#FFF3F3",
                      color: "#E77877",
                      border: "1px solid #FEADAC",
                      fontWeight: 500,
                      fontSize: "12px",
                      borderRadius: "12px",
                      height: "24px",

                      boxSizing: "border-box",
                    }}
                  />
                )}
              </Box>

              <Typography
                variant="body2"
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  "& > span": { marginRight: "12px", marginBottom: "2px" },
                  "& .label": { marginRight: "4px" },
                }}
              >
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{ color: "#2D2D2D", fontWeight: 500 }}
                  >
                    Member ID:
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#403C6F", fontWeight: 700 }}
                  >
                    {memberData?.subscriberNum
                      ? maskAllButLast(
                          memberData.subscriberNum,
                          2,
                          isShowPhiEnabled,
                        )
                      : "N/A"}
                  </Box>
                </span>
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{ color: "#2D2D2D", fontWeight: 500 }}
                  >
                    Person Code:
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#403C6F", fontWeight: 700 }}
                  >
                    {memberData?.personCode ||
                      (memberData?.id
                        ? `${memberData.id}-Subscriber/Policy Holder`
                        : "N/A")}
                  </Box>
                </span>
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  "& .label": { marginRight: "4px" },
                  "& > span": {
                    display: "inline-flex",
                    alignItems: "center",
                    marginRight: "12px",
                    marginBottom: "2px",
                  },
                }}
              >
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{ color: "#2D2D2D", fontWeight: 500 }}
                  >
                    Gender
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#403C6F", fontWeight: 700 }}
                  >
                    {(memberData?.sex || memberData?.gender) &&
                    !["unknown", "undefined", ""].includes(
                      String(memberData.sex || memberData.gender).toLowerCase(),
                    )
                      ? capitalizeFirstLetter(
                          maskAll(
                            memberData.sex || memberData.gender,
                            isShowPhiEnabled,
                          ),
                        )
                      : "N/A"}
                  </Box>
                </span>
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{ color: "#2D2D2D", fontWeight: 500 }}
                  >
                    Age
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#403C6F", fontWeight: 700 }}
                  >
                    {(() => {
                      if (loading.member) return "N/A";
                      return memberData?.age
                        ? maskAll(memberData.age, isShowPhiEnabled)
                        : "N/A";
                    })()}
                  </Box>
                </span>
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{ color: "#2D2D2D", fontWeight: 500 }}
                  >
                    Date of Birth
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#403C6F", fontWeight: 700 }}
                  >
                    {(() => {
                      if (!memberData?.dob) return "N/A";
                      if (isShowPhiEnabled) {
                        try {
                          const d = new Date(memberData.dob);
                          if (!Number.isNaN(d.getTime())) {
                            return d.toLocaleDateString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                              timeZone: "UTC",
                            });
                          }
                        } catch (e) {
                          console.error("Error formatting date:", e);
                        }
                        return memberData.dob;
                      }
                      return maskDOB(memberData.dob, false);
                    })()}
                  </Box>
                </span>
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{ color: "#2D2D2D", fontWeight: 500 }}
                  >
                    Known Allergies
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      color: "#403C6F",
                      fontWeight: 700,
                      maxWidth: "200px",
                      display: "inline-block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {(() => {
                      let allergies = [];
                      if (Array.isArray(memberData?.allergies)) {
                        allergies = memberData.allergies;
                      } else if (memberData?.allergies) {
                        allergies = [memberData.allergies];
                      }

                      if (allergies.length === 0) {
                        return <span>N/A</span>;
                      }
                      const allAllergies = allergies
                        .flatMap((allergy) =>
                          String(allergy)
                            .split(",")
                            .map((item) => item.trim()),
                        )
                        .filter(Boolean);

                      const maskedAllergies = allAllergies?.map((allergy) => {
                        const words = allergy.split(/\s+/).filter(Boolean);
                        if (words.length === 0) return "";

                        // First word shows initial only, rest are fully masked
                        const maskedWords = words.map((word, index) => {
                          if (index === 0) {
                            return maskShowInitialOnly(word, isShowPhiEnabled);
                          }
                          return maskAll(word, isShowPhiEnabled);
                        });

                        return maskedWords.join(" ");
                      });

                      const allergiesText = maskedAllergies.join(", ");
                      const shouldTruncate = allergiesText.length > 15;

                      return (
                        <Tooltip
                          title={allergiesText}
                          arrow
                          disableHoverListener={!shouldTruncate}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: "200px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "bottom",
                            }}
                          >
                            {allergiesText}
                          </span>
                        </Tooltip>
                      );
                    })()}
                  </Box>
                </span>
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{ color: "#2D2D2D", fontWeight: 500 }}
                  >
                    Time Zone
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#403C6F", fontWeight: 700 }}
                  >
                    {memberData?.contact?.timeZone ||
                      memberData?.timezone ||
                      "N/A"}
                  </Box>
                </span>
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{ color: "#2D2D2D", fontWeight: 500 }}
                  >
                    Language
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#403C6F", fontWeight: 700 }}
                  >
                    {memberData?.contact?.language ||
                      memberData?.language ||
                      "N/A"}
                  </Box>
                </span>
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  "& .label": { marginRight: "4px" },
                  "& > span": { marginRight: "12px", marginBottom: "2px" },
                }}
              >
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{ color: "#2D2D2D", fontWeight: 500 }}
                  >
                    Phone Number
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#403C6F", fontWeight: 700 }}
                  >
                    {formatPhoneNumber(
                      memberData?.contact?.phone ||
                        memberData?.providerPhone ||
                        "",
                      isShowPhiEnabled,
                    ) || "N/A"}
                  </Box>
                </span>
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{ color: "#2D2D2D", fontWeight: 500 }}
                  >
                    Email
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#403C6F", fontWeight: 700 }}
                  >
                    {memberData?.contact?.email &&
                    !memberData.contact.email.includes("undefined")
                      ? maskEmail(memberData.contact.email, isShowPhiEnabled)
                      : "N/A"}
                  </Box>
                </span>
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{ color: "#2D2D2D", fontWeight: 500 }}
                  >
                    Address
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#403C6F", fontWeight: 700 }}
                  >
                    {(() => {
                      const address =
                        memberData?.contact?.address || memberData?.address;

                      if (!address) return "N/A";

                      if (typeof address === "string") return address;

                      const streetObj = address.street || {};

                      const line1 = streetObj.line1 || address.line1 || "";
                      const line2 = streetObj.line2 || address.line2 || "";
                      const city = streetObj.city || address.city || "";
                      const state = streetObj.state || address.state || "";
                      const postalCode =
                        streetObj.postalCode ||
                        address.postalCode ||
                        address.zip ||
                        "";

                      // Apply masking to address fields
                      const maskedLine1 = line1
                        ? maskAddressLine(line1, isShowPhiEnabled)
                        : "";
                      const maskedLine2 = line2
                        ? maskAddressLine(line2, isShowPhiEnabled)
                        : "";
                      const maskedCity = city
                        ? maskCity(city, isShowPhiEnabled)
                        : "";
                      const maskedState = state
                        ? maskState(state, isShowPhiEnabled)
                        : "";
                      const maskedPostalCode = postalCode
                        ? maskPostalCode(postalCode, isShowPhiEnabled)
                        : "";

                      const addressParts = [
                        maskedLine1,
                        maskedLine2,
                        [maskedCity, maskedState, maskedPostalCode]
                          .filter(Boolean)
                          .join(" "),
                      ].filter(Boolean);

                      return addressParts.join(", ") || "N/A";
                    })()}
                  </Box>
                </span>
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: "16px",
                width: "100%",
                flex: 1,
                minHeight: { xs: "400px", md: "495px" }, // Ensure minimum height on small screens
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  borderRadius: "16px",
                  border: "1px solid #E0E0E0",
                  backgroundColor: "white",
                  boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
                  p: "20px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  minHeight: "300px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "#403C6F",
                  }}
                >
                  {source === "memberMonitoring"
                    ? "Opportunities to monitor"
                    : "Top Opportunities"}
                </Typography>
                <Divider />
                <TopOpportunities
                  opportunities={opportunities}
                  source={source}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  borderRadius: "16px",
                  border: "1px solid #E0E0E0",
                  backgroundColor: "white",
                  fontWeight: 600,
                  fontSize: "16px",
                  display: "flex",
                  flexDirection: "column",
                  // height: "100%",
                  minHeight: "400px",
                  "& .MuiBox-root": {
                    overflow: "visible !important",
                  },
                }}
                component="section"
                aria-label="Notes"
              >
                <Box sx={{ flex: 1 }}>{notesContent}</Box>
              </Box>
            </Box>
          </ContentWrapper>
        </Box>
      </PopupContainer>
    </Overlay>
  );
};

MemberQuickview.propTypes = {
  onClose: PropTypes.func,
  memberData: PropTypes.shape({
    memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    member: PropTypes.object,
    language: PropTypes.string,
    timeZone: PropTypes.string,
    sex: PropTypes.string,
    gender: PropTypes.string,
    age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dob: PropTypes.string,
    dateOfBirth: PropTypes.string,
    contact: PropTypes.shape({
      phone: PropTypes.string,
      email: PropTypes.string,
      language: PropTypes.string,
      timeZone: PropTypes.string,
      address: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      sex: PropTypes.string,
      gender: PropTypes.string,
    }),
    knownAllergies: PropTypes.array,
    allergies: PropTypes.array,
    status: PropTypes.string,
    doNotCall: PropTypes.bool,
    subscriberNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    personCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onNoteUpdate: PropTypes.func,
  source: PropTypes.string,
};

export default MemberQuickview;
